import { MAP_REGION_CN, MAP_REGION_MY } from "@/utils/config";

/** 中国全图概览范围（西南角 / 东北角） */
export const CN_OVERVIEW_BOUNDS = {
  sw: [73.5, 17.5],
  ne: [135.0, 53.5],
};

/** 马来西亚全图概览范围（含东马） */
export const MY_OVERVIEW_BOUNDS = {
  sw: [99.6, 0.85],
  ne: [119.3, 7.4],
};

/** MapTiler / 天地图马来 Tab 概览 zoom 上限（与天地图 clamp 一致） */
export const MY_OVERVIEW_MAX_ZOOM = 6;
export const MY_OVERVIEW_FALLBACK_ZOOM = 5;

/** 手机端整体预览 fitBounds 固定 padding（面板收起态） */
export const MOBILE_OVERVIEW_MAP_PADDING = {
  top: 72,
  bottom: 120,
  left: 24,
  right: 24,
};

/** 清除 MapLibre 相机 padding，避免 focus 后残留导致 fitBounds 偏移 */
export const clearMapCameraPadding = (map) => {
  if (!map) return;
  map.stop();
  if (typeof map.setPadding === "function") {
    map.setPadding({ top: 0, bottom: 0, left: 0, right: 0 });
  }
};

/** 马来西亚整体预览：在 fitBounds 基础上再拉近一点（仅动画重置时可选） */
export const MY_OVERVIEW_ZOOM_BUMP = 0;

/** PC 端中国 Tab 概览略放大（大屏 + 左侧列表，可更近一些） */
export const PC_CN_OVERVIEW_MAX_ZOOM = 4;
export const PC_CN_OVERVIEW_FALLBACK_ZOOM = 3;
export const PC_CN_OVERVIEW_ZOOM_BUMP = 1;

/** PC 端 MapTiler fitBounds 默认 padding（左侧留给列表面板） */
export const PC_MAP_FIT_PADDING = {
  top: 90,
  bottom: 60,
  left: 480,
  right: 60,
};

/**
 * PC 马来 Tab：门店集中在吉隆坡（西侧），地图整体略向左偏（用户视角）
 * - 增大右 padding：可视区域左移，吉隆坡不至于贴边
 * - 中心经度西移：与 padding 配套
 */
export const PC_MY_OVERVIEW_EXTRA_RIGHT_PADDING = 96;
export const PC_MY_OVERVIEW_CENTER_LNG_OFFSET = -0.85;

export const getPcMapFitPadding = (mapRegion) => {
  const padding = { ...PC_MAP_FIT_PADDING };
  if (mapRegion === MAP_REGION_MY) {
    padding.right += PC_MY_OVERVIEW_EXTRA_RIGHT_PADDING;
  }
  return padding;
};

const readMapCenterLngLat = (map) => {
  const center = map.getCenter?.();
  if (!center) return null;
  const lng = Number(center.lng ?? center.getLng?.());
  const lat = Number(center.lat ?? center.getLat?.());
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return { lng, lat };
};

/** PC 马来 Tab 概览：fit 后将地图中心略向西移（用户视角整体左移） */
export const applyPcMalaysiaOverviewCenterBias = (
  map,
  { animated = false, duration = 0 } = {}
) => {
  if (!map) return;
  const center = readMapCenterLngLat(map);
  if (!center) return;
  const nextLng = center.lng + PC_MY_OVERVIEW_CENTER_LNG_OFFSET;
  const animDuration = animated ? duration || 280 : 0;

  if (typeof map.easeTo === "function" && typeof map.getZoom === "function") {
    map.easeTo({
      center: [nextLng, center.lat],
      zoom: map.getZoom(),
      duration: animDuration,
    });
    return;
  }

  if (
    typeof window !== "undefined" &&
    window.T &&
    typeof map.centerAndZoom === "function"
  ) {
    map.centerAndZoom(new window.T.LngLat(nextLng, center.lat), map.getZoom?.());
  }
};

const schedulePcMalaysiaOverviewCenterBias = (map, options = {}) => {
  const apply = () => applyPcMalaysiaOverviewCenterBias(map, options);
  apply();
  requestAnimationFrame(apply);
  setTimeout(apply, 100);
};

/** 整体预览 fitBounds 时的 maxZoom */
export const getRegionOverviewMaxZoom = (mapRegion, pc = false) => {
  if (mapRegion === MAP_REGION_CN) {
    return pc ? PC_CN_OVERVIEW_MAX_ZOOM : 4;
  }
  if (mapRegion === MAP_REGION_MY) return MY_OVERVIEW_MAX_ZOOM;
  return 7;
};

/** 无有效坐标时的默认概览 zoom */
export const getRegionOverviewFallbackZoom = (mapRegion, pc = false) => {
  if (mapRegion === MAP_REGION_CN) {
    return pc ? PC_CN_OVERVIEW_FALLBACK_ZOOM : 3;
  }
  if (mapRegion === MAP_REGION_MY) return MY_OVERVIEW_FALLBACK_ZOOM;
  return 5;
};

/** fitBounds 前合并中/马全国边界，保证 Tab 切换显示整国 */
export const mergeRegionOverviewBounds = (bounds, mapRegion) => {
  if (mapRegion === MAP_REGION_CN) {
    bounds.extend(CN_OVERVIEW_BOUNDS.sw);
    bounds.extend(CN_OVERVIEW_BOUNDS.ne);
    return true;
  }
  if (mapRegion === MAP_REGION_MY) {
    bounds.extend(MY_OVERVIEW_BOUNDS.sw);
    bounds.extend(MY_OVERVIEW_BOUNDS.ne);
    return true;
  }
  return false;
};

/** 选中门店：同省多店 fitBounds 时的 maxZoom */
export const getDealerFocusMaxZoom = (mapRegion) => {
  if (mapRegion === MAP_REGION_CN) return 13;
  if (mapRegion === MAP_REGION_MY) return 11;
  return 11;
};

/** 选中门店：单点 easeTo 时的 zoom（比省份视角更近，≥15 可显示 3D 建筑） */
export const getDealerFocusSingleZoom = (mapRegion) => {
  if (mapRegion === MAP_REGION_CN) return 15;
  if (mapRegion === MAP_REGION_MY) return 14;
  return 14;
};

/** 天地图查看境外（如马来 Tab）：概览/setViewport 后允许的最大 zoom */
export const TIANDITU_ABROAD_OVERVIEW_MAX_ZOOM = MY_OVERVIEW_MAX_ZOOM;

/** 天地图查看境外：选中单店时的 zoom（过大易瓦片空白） */
export const getTiandituDealerFocusZoom = (mapRegion) => {
  if (mapRegion === MAP_REGION_CN) return getDealerFocusSingleZoom(MAP_REGION_CN);
  if (mapRegion === MAP_REGION_MY) return 8;
  return 8;
};

/** 天地图查看境外：无边界时的默认概览 zoom */
export const getTiandituRegionOverviewFallbackZoom = (mapRegion, pc = false) => {
  if (mapRegion === MAP_REGION_CN) {
    return getRegionOverviewFallbackZoom(MAP_REGION_CN, pc);
  }
  if (mapRegion === MAP_REGION_MY) return MY_OVERVIEW_FALLBACK_ZOOM;
  return 5;
};

/** setViewport 等自动 fit 后，限制天地图在境外区域的 zoom 上限 */
export const clampTiandituAbroadZoom = (map, mapRegion) => {
  if (!map || mapRegion !== MAP_REGION_MY) return;
  const maxZoom = TIANDITU_ABROAD_OVERVIEW_MAX_ZOOM;
  const apply = () => {
    const current = map.getZoom?.();
    if (!Number.isFinite(current) || current <= maxZoom) return;
    const center = map.getCenter?.();
    if (center && typeof map.centerAndZoom === "function") {
      map.centerAndZoom(center, maxZoom);
      return;
    }
    if (typeof map.setZoom === "function") {
      map.setZoom(maxZoom);
    }
  };
  apply();
  requestAnimationFrame(apply);
  setTimeout(apply, 80);
};

/** 天地图 fit 全部门店视野（境外自动限制 zoom） */
export const fitTiandituMapOverview = (
  map,
  { points, mapRegion, fallback, pc = false }
) => {
  if (!map || typeof window === "undefined" || !window.T) return;
  if (points.length > 0 && typeof map.setViewport === "function") {
    map.setViewport(points);
    if (mapRegion === MAP_REGION_CN && pc) {
      clampTiandituPcChinaOverviewZoom(map, mapRegion);
    } else if (mapRegion === MAP_REGION_MY) {
      clampTiandituAbroadZoom(map, mapRegion);
      if (pc) schedulePcMalaysiaOverviewCenterBias(map);
    } else {
      clampTiandituAbroadZoom(map, mapRegion);
    }
    return;
  }
  const fallbackLng =
    fallback.lng +
    (pc && mapRegion === MAP_REGION_MY ? PC_MY_OVERVIEW_CENTER_LNG_OFFSET : 0);
  map.centerAndZoom(
    new window.T.LngLat(fallbackLng, fallback.lat),
    getTiandituRegionOverviewFallbackZoom(mapRegion, pc)
  );
};

/** PC 天地图中国 Tab：setViewport 后略放大 */
export const clampTiandituPcChinaOverviewZoom = (map, mapRegion) => {
  if (!map || mapRegion !== MAP_REGION_CN) return;
  const minZoom = getRegionOverviewFallbackZoom(MAP_REGION_CN, true);
  const maxZoom = getRegionOverviewMaxZoom(MAP_REGION_CN, true);
  const apply = () => {
    const current = map.getZoom?.();
    if (!Number.isFinite(current)) return;
    let target = current;
    if (current < minZoom) target = minZoom;
    if (current + PC_CN_OVERVIEW_ZOOM_BUMP <= maxZoom) {
      target = Math.max(
        target,
        Math.min(current + PC_CN_OVERVIEW_ZOOM_BUMP, maxZoom)
      );
    }
    if (target === current) return;
    const center = map.getCenter?.();
    if (center && typeof map.centerAndZoom === "function") {
      map.centerAndZoom(center, target);
    } else if (typeof map.setZoom === "function") {
      map.setZoom(target);
    }
  };
  apply();
  requestAnimationFrame(apply);
  setTimeout(apply, 80);
  setTimeout(apply, 200);
};

/** 选中门店：居中到当前店（含 padding，避开侧栏/底栏） */
export const easeMapToSelectedDealer = (
  map,
  { lng, lat, mapRegion, padding, duration = 400 }
) => {
  if (!map || !Number.isFinite(lng) || !Number.isFinite(lat)) return;
  map.easeTo({
    center: [lng, lat],
    zoom: getDealerFocusSingleZoom(mapRegion),
    padding,
    duration,
  });
};

export const bumpChinaOverviewZoom = (map, { animated = false, pc = false } = {}) => {
  if (!map || !pc) return;
  const maxZoom = getRegionOverviewMaxZoom(MAP_REGION_CN, true);
  const current = map.getZoom();
  const next = Math.min(current + PC_CN_OVERVIEW_ZOOM_BUMP, maxZoom);
  if (next <= current) return;
  map.easeTo({ zoom: next, duration: animated ? 280 : 0 });
};

export const bumpMalaysiaOverviewZoom = (map, { animated = false } = {}) => {
  if (!map) return;
  const maxZoom = getRegionOverviewMaxZoom(MAP_REGION_MY);
  const current = map.getZoom();
  const next = Math.min(current + MY_OVERVIEW_ZOOM_BUMP, maxZoom);
  if (next <= current) return;
  map.easeTo({ zoom: next, duration: animated ? 280 : 0 });
};

export const afterOverviewFitBounds = (
  map,
  mapRegion,
  { animated = false, duration = 0, pc = false } = {}
) => {
  if (!map) return;

  const applyMyPcBias = () => {
    if (pc && mapRegion === MAP_REGION_MY) {
      schedulePcMalaysiaOverviewCenterBias(map, { animated, duration });
    }
  };

  if (!animated) {
    applyMyPcBias();
    return;
  }
  if (mapRegion === MAP_REGION_CN && pc) {
    const applyBump = () => bumpChinaOverviewZoom(map, { animated, pc: true });
    map.once("moveend", applyBump);
    requestAnimationFrame(applyBump);
    return;
  }
  if (mapRegion !== MAP_REGION_MY) return;
  if (animated && duration > 0 && MY_OVERVIEW_ZOOM_BUMP > 0) {
    map.once("moveend", () => {
      bumpMalaysiaOverviewZoom(map, { animated: true });
      applyMyPcBias();
    });
    return;
  }
  bumpMalaysiaOverviewZoom(map, { animated: false });
  applyMyPcBias();
};
